import { API_CONFIG } from '../config/api';
import { logInfo, logError } from './logtail';
import toast from 'react-hot-toast';

export interface GDriveAuthResponse {
  success: boolean;
  authUrl?: string;
  errorMessage?: string;
}

export interface GDriveCallbackResponse {
  success: boolean;
  errorMessage?: string;
  accessToken?: string;
}

export const GDRIVE_RETURN_KEY = 'cassette:gdriveReturnTo';

export function saveGDriveReturnPath(path?: string) {
  const target = path ?? `${window.location.pathname}${window.location.search}`;
  sessionStorage.setItem(GDRIVE_RETURN_KEY, target);
}

export function consumeGDriveReturnPath(fallback = '/dashboard'): string {
  const path = sessionStorage.getItem(GDRIVE_RETURN_KEY);
  sessionStorage.removeItem(GDRIVE_RETURN_KEY);
  if (!path || !path.startsWith('/')) {
    return fallback;
  }
  return path;
}

const realStartOAuth = async (userId?: string): Promise<GDriveAuthResponse> => {
  try {
    logInfo('Starting Google Drive OAuth with backend', { userId });
    const params = new URLSearchParams();
    if (userId) {
      params.set('userId', userId);
    }
    params.set('frontendUrl', window.location.origin);
    const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GDRIVE_OAUTH_START}?${params.toString()}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    const result = await response.json();
    logInfo('Google Drive OAuth started successfully', { success: result.success });
    return result;
  } catch (error) {
    logError('Failed to start Google Drive OAuth', { 
      error: error instanceof Error ? error.message : String(error),
      endpoint: API_CONFIG.ENDPOINTS.GDRIVE_OAUTH_START 
    });
    throw error;
  }
};

const realHandleCallback = async (code: string): Promise<GDriveCallbackResponse> => {
  try {
    logInfo('Handling Google Drive OAuth callback with backend', { codeLength: code.length });
    const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GDRIVE_OAUTH_CALLBACK}?code=${encodeURIComponent(code)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    const result = await response.json();
    logInfo('Google Drive OAuth callback handled successfully', { success: result.success });
    return result;
  } catch (error) {
    logError('Failed to handle Google Drive OAuth callback', { 
      error: error instanceof Error ? error.message : String(error),
      endpoint: API_CONFIG.ENDPOINTS.GDRIVE_OAUTH_CALLBACK,
      codeLength: code.length
    });
    throw error;
  }
};

export const startGDriveOAuth = realStartOAuth;
export const handleGDriveCallback = realHandleCallback;

let reauthInFlight = false;

/**
 * Single entry point when Google Drive tokens are stale (upload/API/websocket).
 * Avoids duplicate unlink + OAuth storms from multiple listeners.
 */
export async function handleGDriveReauthNeeded(options?: {
  unlink?: () => Promise<void>;
  navigate?: (path: string) => void;
  message?: string;
}) {
  if (reauthInFlight) {
    return;
  }
  reauthInFlight = true;

  const message = options?.message
    ?? 'Your Google Drive connection expired. Reconnect to continue uploading.';

  toast.error(message, { duration: 5000, id: 'gdrive-reauth-needed' });
  saveGDriveReturnPath();

  try {
    if (options?.unlink) {
      await options.unlink();
    }
  } catch (error) {
    logError('Failed to clear expired Google Drive connection', {
      error: error instanceof Error ? error.message : String(error),
    });
  }

  if (options?.navigate) {
    options.navigate('/auth/storage?reason=expired&provider=gdrive');
  } else {
    window.location.assign('/auth/storage?reason=expired&provider=gdrive');
  }

  window.setTimeout(() => {
    reauthInFlight = false;
  }, 3000);
}

/**
 * Redirect-based Google Drive OAuth (recommended for all platforms)
 */
export const startGDriveOAuthRedirect = async (userId?: string): Promise<void> => {
  try {
    logInfo('Starting Google Drive OAuth redirect flow', { userId });
    saveGDriveReturnPath();

    const response = await realStartOAuth(userId);
    if (response.success && response.authUrl) {
      const url = new URL(response.authUrl);
      if (url.protocol !== 'https:' || (!url.hostname.endsWith('.google.com') && url.hostname !== 'accounts.google.com')) {
        throw new Error('Invalid OAuth URL domain');
      }

      logInfo('Redirecting to Google Drive OAuth', { authUrl: response.authUrl });
      window.location.href = response.authUrl;
    } else {
      throw new Error(response.errorMessage || 'Failed to start OAuth');
    }
  } catch (error) {
    logError('Failed to start Google Drive OAuth redirect', { 
      error: error instanceof Error ? error.message : String(error) 
    });
    throw error;
  }
};

/**
 * @deprecated Use startGDriveOAuthRedirect instead
 */
export const openGDriveOAuth = async (userId?: string): Promise<void> => {
  logInfo('openGDriveOAuth called - redirecting to new redirect flow');
  return startGDriveOAuthRedirect(userId);
};

export const registerGDriveOAuthExpiredHandler = (
  unlinkCallback?: () => Promise<void>,
  navigate?: (path: string) => void,
) => {
  logInfo('Registering GDrive OAuth expired handler');
  window.addEventListener('gdrive-oauth-expired', () => {
    logInfo('GDrive OAuth expired event received');
    void handleGDriveReauthNeeded({ unlink: unlinkCallback, navigate });
  });
};
